import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isValidSlug, slugify } from "../../shared/slug";
import { api, type ProductWithDemos } from "../api";
import { IconCopy, IconTrash } from "../icons";
import { useShell } from "./AdminShell";
import ProductForm, { type ProductDraft } from "./ProductForm";

export default function ProductPage() {
  const { productId = "" } = useParams();
  const { refreshNav } = useShell();
  const nav = useNavigate();
  const [product, setProduct] = useState<ProductWithDemos | null>(null);
  const [draft, setDraft] = useState<ProductDraft | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [newDemo, setNewDemo] = useState({ title: "", slug: "" });
  const [touched, setTouched] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [deletingDemoId, setDeletingDemoId] = useState<string | null>(null);


  useEffect(() => {
    api.product(productId).then((p) => {
      setProduct(p);
      setDraft({ name: p.name, slug: p.slug, description: p.description, brandColor: p.brandColor, logoKey: p.logoKey });
    });
  }, [productId]);

  if (!product || !draft) return <p className="muted">Loading…</p>;

  const save = async () => {
    try {
      await api.updateProduct(product.id, draft);
      await refreshNav();
      setMsg({ ok: true, text: "Saved" });
    } catch (e: any) {
      setMsg({ ok: false, text: e.message });
    }
  };
  const remove = async () => {
    if (!confirm(`Delete ${product.name} and all ${product.demos.length} of its demos? Published links will stop working. This can't be undone.`)) return;
    await api.deleteProduct(product.id);
    await refreshNav();
    nav("/admin");
  };
  const addDemo = async () => {
    try {
      const d = await api.createDemo(product.id, newDemo);
      await refreshNav();
      nav(`/admin/demos/${d.id}/survey?new=1`);
    } catch (e: any) {
      setMsg({ ok: false, text: e.message });
    }
  };
  const duplicate = async (demoId: string) => {
    try {
      setDuplicatingId(demoId);
      const cloned = await api.duplicateDemo(demoId);
      await refreshNav();
      nav(`/admin/demos/${cloned.demo.id}/basics`);
    } catch (e: any) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setDuplicatingId(null);
    }
  };

  const removeDemo = async (demoId: string, demoTitle: string) => {
    if (!confirm(`Delete "${demoTitle}"? Its screens, survey and analytics are removed and the public link stops working.`)) return;
    try {
      setDeletingDemoId(demoId);
      await api.deleteDemo(demoId);
      await refreshNav();
      setProduct(await api.product(product.id));
    } catch (e: any) {
      alert(e.message);
    } finally {
      setDeletingDemoId(null);
    }
  };

  return (
    <div className="stack" style={{ maxWidth: 860 }}>
      <div className="page-head">
        <div><Link to="/admin" className="crumb">All demos</Link><h1>{product.name}</h1></div>
      </div>

      <section className="panel stack">
        <h2>Demos</h2>
        {product.demos.map((d) => (
          <div key={d.id} className="row">
            <Link to={`/admin/demos/${d.id}/basics`}>{d.title}</Link>
            <span className={`pill ${d.status === "published" ? "pill-live" : "pill-draft"}`}>{d.status === "published" ? "Live" : "Draft"}</span>
            <span className="spacer" />
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              title="Duplicate demo"
              disabled={duplicatingId === d.id || deletingDemoId === d.id}
              onClick={() => duplicate(d.id)}
            >
              <IconCopy />{duplicatingId === d.id ? "Duplicating…" : "Duplicate"}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost btn-danger"
              title="Delete demo"
              disabled={deletingDemoId === d.id || duplicatingId === d.id}
              onClick={() => removeDemo(d.id, d.title)}
            >
              <IconTrash />{deletingDemoId === d.id ? "Deleting…" : "Delete"}
            </button>
          </div>
        ))}

        <div className="grid-2" style={{ alignItems: "end" }}>
          <label className="field"><span>New demo title</span>
            <input type="text" value={newDemo.title} onChange={(e) => setNewDemo({ title: e.target.value, slug: touched ? newDemo.slug : slugify(e.target.value) })} />
          </label>
          <label className="field"><span>URL name</span>
            <input type="text" value={newDemo.slug} onChange={(e) => { setTouched(true); setNewDemo({ ...newDemo, slug: slugify(e.target.value) }); }} />
          </label>
        </div>
        <div><button className="btn btn-primary" disabled={!newDemo.title.trim() || !isValidSlug(newDemo.slug)} onClick={addDemo}>Add demo</button></div>
      </section>

      <section className="panel stack">
        <h2>Product settings</h2>
        <ProductForm value={draft} onChange={setDraft} slugLocked />
        <p className="small muted">Changing the URL name breaks links you've already shared for this product's demos.</p>
        {msg && <div className={msg.ok ? "ok-box" : "error-box"}>{msg.text}</div>}
        <div className="row">
          <button className="btn btn-primary" onClick={save} disabled={!draft.name.trim() || !isValidSlug(draft.slug)}>Save changes</button>
          <span className="spacer" />
          <button className="btn btn-danger" onClick={remove}>Delete product</button>
        </div>
      </section>
    </div>
  );
}
